import React, { useState, useEffect } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { User } from '../../types/cabinet.types';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface EditPersonalModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onSave: (data: Partial<User>) => Promise<boolean>;
}

export const EditPersonalModal: React.FC<EditPersonalModalProps> = ({ isOpen, onClose, user, onSave }) => {
  const { t } = useI18n();
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [pesel, setPesel] = useState(user?.pesel || '');
  const [passport, setPassport] = useState(user?.passport || '');
  const [sex, setSex] = useState<'female' | 'male' | ''>(user?.sex || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFirstName(user?.firstName || '');
      setLastName(user?.lastName || '');
      setPesel(user?.pesel || '');
      setPassport(user?.passport || '');
      setSex(user?.sex || '');
    }
  }, [isOpen, user]);

  const handleSave = async () => {
    setIsSaving(true);
    const success = await onSave({ firstName, lastName, pesel, passport, sex: sex as 'female' | 'male' });
    setIsSaving(false);
    if (success) {
      onClose();
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={t('dane_personal')} 
      subtitle={t('edit_your')}
    >
      <div className="cab-field">
        <label>{t('firstname')}</label>
        <input 
          type="text" 
          className="cab-input" 
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          disabled={isSaving}
        />
      </div>

      <div className="cab-field">
        <label>{t('lastname')}</label>
        <input 
          type="text" 
          className="cab-input" 
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={isSaving}
        />
      </div>
      
      <div className="cab-field">
        <label>{t('pesel')}</label>
        <input 
          type="text" 
          className="cab-input" 
          value={pesel}
          onChange={(e) => setPesel(e.target.value)}
          disabled={isSaving}
        />
      </div>

      <div className="cab-field">
        <label>{t('passport')}</label>
        <input 
          type="text" 
          className="cab-input" 
          value={passport}
          onChange={(e) => setPassport(e.target.value)}
          disabled={isSaving}
        />
      </div>

      <div className="cab-field" style={{ marginBottom: '24px' }}>
        <label>{t('sex')}</label>
        <select 
          className="cab-input" 
          value={sex}
          onChange={(e) => setSex(e.target.value as 'female' | 'male' | '')}
          disabled={isSaving}
          style={{ appearance: 'auto' }}
        >
          <option value="">--</option>
          <option value="female">{t('female')}</option>
          <option value="male">{t('male')}</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <Button variant="ghost" onClick={onClose} disabled={isSaving} style={{ flex: 1 }}>
          {t('cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave} isLoading={isSaving} style={{ flex: 1 }}>
          {t('save')}
        </Button>
      </div>
    </Modal>
  );
};
